import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaUser, FaTools, FaBriefcase, FaStore } from "react-icons/fa";
import { toast } from "react-toastify"; // ✅ Toastify import
import "react-toastify/dist/ReactToastify.css"; // ✅ Toastify CSS
import "../styles/CategorySelection.css";

const categories = [
  { name: "Individual", icon: <FaUser />, path: "/register/individual" },
  { name: "Installer", icon: <FaTools />, path: "/register/installer" },
  { name: "Vendor", icon: <FaStore />, path: "/register/vendor" },
  { name: "Business", icon: <FaBriefcase />, path: "/" },
];

export default function CategorySelection() {
  const navigate = useNavigate();
  const cardRefs = useRef([]);
  const timeoutsRef = useRef([]);
  const intervalRef = useRef(null);
  const [pointerStyle, setPointerStyle] = useState({
    left: -100,
    top: -100,
    opacity: 0,
  });
  const [activeIndex, setActiveIndex] = useState(null);

  // Position the fixed pointer slightly inside each card
  const moveToCard = (index) => {
    const el = cardRefs.current[index];
    if (!el) return;
    const rect = el.getBoundingClientRect();

    const left = Math.round(window.scrollX + rect.left + 10);
    const top = Math.round(window.scrollY + rect.top + rect.height * 0.5 - 12);

    setPointerStyle({ left, top, opacity: 1 });
    setActiveIndex(index);
  };

  useEffect(() => {
    const CARD_HOLD_MS = 900;
    const REPEAT_INTERVAL_MS = 10000;

    function clearAllTimeouts() {
      timeoutsRef.current.forEach((t) => clearTimeout(t));
      timeoutsRef.current = [];
    }

    function runSequence() {
      clearAllTimeouts();
      const count = categories.length;
      if (!cardRefs.current[0]) {
        const retry = setTimeout(runSequence, 200);
        timeoutsRef.current.push(retry);
        return;
      }
      for (let i = 0; i < count; i++) {
        const t = setTimeout(() => {
          moveToCard(i);
        }, i * CARD_HOLD_MS);
        timeoutsRef.current.push(t);
      }
      const hideT = setTimeout(() => {
        setPointerStyle((s) => ({ ...s, opacity: 0 }));
        setActiveIndex(null);
      }, count * CARD_HOLD_MS + 250);
      timeoutsRef.current.push(hideT);
    }

    const startup = setTimeout(() => {
      runSequence();
      intervalRef.current = setInterval(runSequence, REPEAT_INTERVAL_MS);
    }, 200);
    timeoutsRef.current.push(startup);

    return () => {
      clearAllTimeouts();
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  // Scroll lock effect
  useEffect(() => {
    const preventScroll = (e) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    };
    document.body.style.overflow = "hidden";
    document.body.style.height = "100%";
    window.addEventListener("wheel", preventScroll, { passive: false });
    window.addEventListener("touchmove", preventScroll, { passive: false });
    return () => {
      document.body.style.overflow = "";
      document.body.style.height = "";
      window.removeEventListener("wheel", preventScroll);
      window.removeEventListener("touchmove", preventScroll);
    };
  }, []);

  return (
    <>
      <style>{`
        html, body {
          overflow: hidden;
          height: 100%;
          touch-action: none;
        }
        .floating-pointer {
          position: fixed;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 22px;
          z-index: 9999;
          transition: left 260ms ease, top 260ms ease, opacity 180ms ease;
          pointer-events: none;
          will-change: left, top, opacity, transform;
          opacity: 0;
        }
        .floating-pointer.visible {
          opacity: 1;
          animation: fp-bounce 0.6s infinite alternate;
        }
        @keyframes fp-bounce {
          from { transform: translateY(0); }
          to { transform: translateY(-6px); }
        }
        .category-card {
          position: relative;
          z-index: 1;
          transition: transform 0.3s ease, box-shadow 0.3s ease, filter 0.3s ease;
        }
        .category-card.pointed {
          transform: scale(1.05);
          box-shadow: 0 8px 20px rgba(0,0,0,0.15);
          filter: brightness(1.05);
        }
      `}</style>

      {/* Floating pointer */}
      <div
        className={`floating-pointer ${pointerStyle.opacity > 0 ? "visible" : ""}`}
        style={{
          left: `${pointerStyle.left}px`,
          top: `${pointerStyle.top}px`,
          opacity: pointerStyle.opacity,
        }}
        aria-hidden="true"
      >
        👉
      </div>

      <div className="category-page" style={{ height: "100vh", overflow: "hidden" }}>
        <div className="category-container">
          <h1 className="category-title">Welcome!</h1>
          <p className="category-subtitle">Select your user category to get started</p>

          <div className="category-grid">
            {categories.map((cat, idx) => (
              <div
                key={cat.name}
                className={`category-card ${activeIndex === idx ? "pointed" : ""}`}
                onClick={() => {
                  if (cat.name === "Business") {
                    toast.info("🚀 Business category coming soon!", {
                      position: "top-right",
                      autoClose: 2500,
                      hideProgressBar: false,
                      closeOnClick: true,
                      pauseOnHover: true,
                      draggable: true,
                      theme: "colored",
                    });
                    return;
                  }
                  navigate(cat.path);
                }}
                ref={(el) => (cardRefs.current[idx] = el)}
              >
                <div className="category-icon">{cat.icon}</div>
                <h3>{cat.name}</h3>
              </div>
            ))}
          </div>

          <div className="signin-link">
            Already have an account?{" "}
            <span onClick={() => navigate("/login")} className="signin-btn">
              Sign In
            </span>
          </div>
        </div>

        <div className="bottom-section">
          <div className="privacy-terms">
            <span onClick={() => navigate("/privacy-policy")}>Privacy Policy</span>{" "}
            |{" "}
            <span onClick={() => navigate("/terms-and-conditions")}>
              Terms & Conditions
            </span>
            <h4>
              &copy; 2025 Globisun Energies - All rights reserved <br />
              Powered by{" "}
              <a
                href="https://globalnetexperts.com.ng/"
                target="_blank"
                rel="noreferrer"
              >
                GlobalNet ICT Solutions
              </a>
            </h4>
          </div>
        </div>
      </div>
    </>
  );
}
